import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { formatINR } from '@/lib/utils';
import { useAuth } from '@/lib/auth';

const empty = {
  name: '', description: '', hourly_rate: 400, open_hour: 6, close_hour: 22, image_url: '', active: true,
  peak_windows: [], sport_id: '',
};

export default function AdminCourts() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState('');
  const [sports, setSports] = useState([]);
  const { toast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const load = () => api.get('/courts', { params: { only_active: false } }).then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get('/sports').then((r) => setSports(r.data)).catch(() => {});
    // super_admin picks which venue a new court belongs to — a venue
    // admin's own venue is inferred server-side, so they never see this
    // control.
    if (isSuperAdmin) {
      api.get('/admin/academies').then((r) => setVenues(r.data)).catch(() => {});
    }
  }, [isSuperAdmin]);

  const sportName = (id) => sports.find((s) => s.id === id)?.name;

  const openNew = () => { setForm({ ...empty, sport_id: sports[0]?.id || '' }); setEditing(null); setVenueId(''); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c, peak_windows: c.peak_windows || [] }); setEditing(c.id); setOpen(true); };

  // Peak-hour pricing: any number of non-overlapping windows (e.g. a
  // morning rush and a separate evening rush), each with its own rate —
  // fully customizable per court. Toggling on pre-fills 2 common sessions
  // as a starting point; the admin can add/remove/edit freely from there.
  const peakEnabled = form.peak_windows.length > 0;
  const togglePeak = (checked) => {
    if (checked) {
      const suggestedRate = Math.round(Number(form.hourly_rate) * 1.3) || 400;
      setForm({
        ...form,
        peak_windows: [
          { start_hour: 6, end_hour: 9, rate: suggestedRate },
          { start_hour: 18, end_hour: 22, rate: suggestedRate },
        ],
      });
    } else {
      setForm({ ...form, peak_windows: [] });
    }
  };
  const setPeakWindow = (i, patch) =>
    setForm({ ...form, peak_windows: form.peak_windows.map((w, idx) => (idx === i ? { ...w, ...patch } : w)) });
  const addPeakWindow = () =>
    setForm({ ...form, peak_windows: [...form.peak_windows, { start_hour: 18, end_hour: 22, rate: Math.round(Number(form.hourly_rate) * 1.3) || 400 }] });
  const removePeakWindow = (i) =>
    setForm({ ...form, peak_windows: form.peak_windows.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!editing && isSuperAdmin && venues.length > 1 && !venueId) {
      toast({ title: 'Choose a venue', description: 'More than one venue exists — pick which one this court belongs to', variant: 'error' });
      return;
    }
    try {
      const payload = {
        ...form,
        hourly_rate: Number(form.hourly_rate),
        open_hour: Number(form.open_hour),
        close_hour: Number(form.close_hour),
        peak_windows: form.peak_windows.map((w) => ({
          start_hour: Number(w.start_hour), end_hour: Number(w.end_hour), rate: Number(w.rate),
        })),
      };
      if (editing) await api.put(`/courts/${editing}`, payload);
      else await api.post('/courts', payload, { params: venueId ? { academy_id: venueId } : {} });
      toast({ title: editing ? 'Court updated' : 'Court added', variant: 'success' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Save failed', description: e.response?.data?.detail || '', variant: 'error' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this court? Existing bookings will remain.')) return;
    try { await api.delete(`/courts/${id}`); toast({ title: 'Court deleted', variant: 'success' }); load(); }
    catch { toast({ title: 'Delete failed', variant: 'error' }); }
  };

  return (
    <div className="space-y-6" data-testid="admin-courts">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Facilities</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Courts</h1>
        </div>
        <Button onClick={openNew} data-testid="add-court-button"><Plus size={14} /> Add Court</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((c) => (
          <div key={c.id} className="bg-surface border border-white/10 flex flex-col">
            <div className="aspect-[5/3] bg-obsidian overflow-hidden">
              {c.image_url ? <img src={c.image_url} alt={c.name} className="w-full h-full object-cover opacity-80" /> : <div className="w-full h-full" />}
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Badge tone={c.active ? 'success' : 'default'}>{c.active ? 'Active' : 'Inactive'}</Badge>
                  {sportName(c.sport_id) && <Badge tone="volt">{sportName(c.sport_id)}</Badge>}
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{c.open_hour}:00 – {c.close_hour}:00</span>
              </div>
              <h3 className="font-heading font-bold text-xl uppercase mb-1">{c.name}</h3>
              <p className="text-sm text-white/50 line-clamp-2 mb-4 flex-1">{c.description || '—'}</p>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-volt font-mono">{formatINR(c.hourly_rate)}<span className="text-white/40 text-xs"> /hr base</span></div>
                  {(c.peak_windows || []).map((w, i) => (
                    <div key={i} className="text-[10px] text-white/40 mt-0.5">Peak {w.start_hour}:00–{w.end_hour}:00 · {formatINR(w.rate)}/hr</div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`edit-court-${c.id}`}><Edit3 size={12} /></Button>
                  <Button variant="danger" size="sm" onClick={() => remove(c.id)} data-testid={`delete-court-${c.id}`}><Trash2 size={12} /></Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-surface border border-white/10 max-w-lg w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="court-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl uppercase">{editing ? 'Edit' : 'New'} Court</h2>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              {!editing && isSuperAdmin && venues.length > 1 && (
                <Field label="Venue">
                  <select
                    value={venueId}
                    onChange={(e) => setVenueId(e.target.value)}
                    className="h-12 w-full border border-white/10 bg-obsidian px-4 text-sm text-white outline-none"
                    data-testid="court-venue"
                  >
                    <option value="">Choose a venue…</option>
                    {venues.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
                  </select>
                </Field>
              )}
              <Field label="Sport">
                <select
                  value={form.sport_id || ''}
                  onChange={(e) => setForm({ ...form, sport_id: e.target.value })}
                  className="h-12 w-full border border-white/10 bg-obsidian px-4 text-sm text-white outline-none"
                  data-testid="court-sport"
                >
                  <option value="">Choose a sport…</option>
                  {sports.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </Field>
              <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Court A – Pro Arena" data-testid="court-name" /></Field>
              <Field label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
              <Field label="Hourly Rate (₹)"><Input type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="court-rate" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Open Hour"><Input type="number" min="0" max="23" value={form.open_hour} onChange={(e) => setForm({ ...form, open_hour: e.target.value })} /></Field>
                <Field label="Close Hour"><Input type="number" min="1" max="24" value={form.close_hour} onChange={(e) => setForm({ ...form, close_hour: e.target.value })} /></Field>
              </div>
              <Field label="Image URL (optional)"><Input value={form.image_url || ''} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" /></Field>

              <div className="border-t border-white/10 pt-4 space-y-4">
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                  <input type="checkbox" checked={peakEnabled} onChange={(e) => togglePeak(e.target.checked)} className="accent-volt" data-testid="court-peak-toggle" />
                  Peak-hour pricing (e.g. ₹400/hr peak, ₹300/hr off-peak)
                </label>
                {peakEnabled && (
                  <div className="space-y-3">
                    {form.peak_windows.map((w, i) => (
                      <div key={i} className="flex items-end gap-3">
                        <Field label="Start"><Input type="number" min="0" max="23" value={w.start_hour} onChange={(e) => setPeakWindow(i, { start_hour: e.target.value })} data-testid={`court-peak-start-${i}`} /></Field>
                        <Field label="End"><Input type="number" min="1" max="24" value={w.end_hour} onChange={(e) => setPeakWindow(i, { end_hour: e.target.value })} data-testid={`court-peak-end-${i}`} /></Field>
                        <Field label="Rate (₹/hr)"><Input type="number" min="0" value={w.rate} onChange={(e) => setPeakWindow(i, { rate: e.target.value })} data-testid={`court-peak-rate-${i}`} /></Field>
                        <Button type="button" variant="outline" size="sm" onClick={() => removePeakWindow(i)} data-testid={`court-peak-remove-${i}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addPeakWindow} data-testid="court-peak-add">
                      <Plus size={14} /> Add peak window
                    </Button>
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-volt" />
                Active (visible to customers)
              </label>
            </div>
            <div className="flex gap-3 mt-8">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={save} className="flex-1" data-testid="save-court">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
